import { ApiResponse } from '@types/index';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
  json?: unknown;
}

async function api<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { params, json, ...fetchOptions } = options;

  let url = path;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    const query = searchParams.toString();
    url = query ? `${path}?${query}` : path;
  }

  const headers = new Headers(fetchOptions.headers || {});
  if (!headers.has('Content-Type') && json) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    body: json ? JSON.stringify(json) : fetchOptions.body,
  });

  const data: ApiResponse<T> = await response.json();

  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.error || 'unknown_error',
      data.message || data.error || 'An error occurred',
      data.errors
    );
  }

  if (!data.ok) {
    throw new ApiError(
      response.status,
      data.error || 'unknown_error',
      data.message || data.error || 'An error occurred',
      data.errors
    );
  }

  return data.data as T;
}

export const apiClient = {
  get: <T,>(path: string, options?: FetchOptions) =>
    api<T>(path, { ...options, method: 'GET' }),

  post: <T,>(path: string, json?: unknown, options?: FetchOptions) =>
    api<T>(path, { ...options, method: 'POST', json }),

  put: <T,>(path: string, json?: unknown, options?: FetchOptions) =>
    api<T>(path, { ...options, method: 'PUT', json }),

  patch: <T,>(path: string, json?: unknown, options?: FetchOptions) =>
    api<T>(path, { ...options, method: 'PATCH', json }),

  delete: <T,>(path: string, options?: FetchOptions) =>
    api<T>(path, { ...options, method: 'DELETE' }),
};
